/**
 * Firestore conversation persistence for global LexCounsel.
 * Uses `lexcounsel_conversations` when rules allow; falls back to legacy `ai_chats`.
 */

import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import type { LexCounselMessage } from '../store/lexCounselStore';

export interface LexCounselConversation {
  id: string;
  caseId: string | null;
  lawyerId: string;
  title: string;
  history: LexCounselMessage[];
  isDefault?: boolean;
  createdAt: string;
  updatedAt: string;
}

const PRIMARY_COLLECTION = 'lexcounsel_conversations';
const LEGACY_COLLECTION = 'ai_chats';

/** Tracks which Firestore subcollection holds each conversation id (this session). */
const collectionByConversationId = new Map<string, string>();

function conversationsRef(workspaceId: string, collectionName: string) {
  return collection(db, 'workspaces', workspaceId, collectionName);
}

function isPermissionError(err: unknown): boolean {
  return (err as { code?: string })?.code === 'permission-denied';
}

function rememberCollection(conversationId: string, collectionName: string): void {
  collectionByConversationId.set(conversationId, collectionName);
}

function stripUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefinedDeep(item)) as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      if (entry !== undefined) {
        out[key] = stripUndefinedDeep(entry);
      }
    }
    return out as T;
  }
  return value;
}

function sanitizeHistory(history: LexCounselMessage[]): LexCounselMessage[] {
  return history.map((message) => {
    const { streaming, ...rest } = message;
    return stripUndefinedDeep({
      ...rest,
      ...(streaming ? { streaming: true } : {}),
    });
  });
}

function resolveCollection(conversationId: string): string {
  return collectionByConversationId.get(conversationId) ?? PRIMARY_COLLECTION;
}

function sortByUpdatedDesc(chats: LexCounselConversation[]): LexCounselConversation[] {
  return [...chats].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

async function loadFromCollection(
  workspaceId: string,
  collectionName: string,
  lawyerId: string,
  caseId: string | null,
): Promise<LexCounselConversation[]> {
  const snap = await getDocs(
    query(conversationsRef(workspaceId, collectionName), where('lawyerId', '==', lawyerId), limit(100)),
  );
  let chats = snap.docs.map((d) => {
    rememberCollection(d.id, collectionName);
    return { id: d.id, ...d.data() } as LexCounselConversation;
  });
  if (caseId) {
    chats = chats.filter((c) => c.caseId === caseId);
  }
  return chats;
}

export async function loadConversations(
  workspaceId: string,
  lawyerId: string,
  caseId: string | null,
): Promise<LexCounselConversation[]> {
  let chats: LexCounselConversation[] = [];
  try {
    chats = await loadFromCollection(workspaceId, PRIMARY_COLLECTION, lawyerId, caseId);
  } catch (err) {
    if (!isPermissionError(err)) throw err;
  }

  if (chats.length === 0) {
    try {
      const legacy = await loadFromCollection(workspaceId, LEGACY_COLLECTION, lawyerId, caseId);
      chats = legacy;
    } catch (err) {
      if (!isPermissionError(err)) throw err;
    }
  }

  return sortByUpdatedDesc(chats).slice(0, 50);
}

export async function createConversation(
  workspaceId: string,
  params: {
    caseId: string | null;
    lawyerId: string;
    title: string;
    history: LexCounselMessage[];
    isDefault?: boolean;
  },
): Promise<string> {
  const now = new Date().toISOString();
  const payload = stripUndefinedDeep({
    caseId: params.caseId,
    lawyerId: params.lawyerId,
    title: params.title,
    history: sanitizeHistory(params.history),
    ...(params.isDefault ? { isDefault: true } : {}),
    createdAt: now,
    updatedAt: now,
  });

  try {
    const ref = await addDoc(conversationsRef(workspaceId, PRIMARY_COLLECTION), payload);
    rememberCollection(ref.id, PRIMARY_COLLECTION);
    return ref.id;
  } catch (err) {
    if (!isPermissionError(err)) throw err;
    const ref = await addDoc(conversationsRef(workspaceId, LEGACY_COLLECTION), payload);
    rememberCollection(ref.id, LEGACY_COLLECTION);
    return ref.id;
  }
}

export async function updateConversation(
  workspaceId: string,
  conversationId: string,
  history: LexCounselMessage[],
  title?: string,
): Promise<void> {
  const payload: Record<string, unknown> = {
    history: sanitizeHistory(history),
    updatedAt: new Date().toISOString(),
  };
  if (title) payload.title = title;

  const collectionName = resolveCollection(conversationId);
  try {
    await updateDoc(doc(db, 'workspaces', workspaceId, collectionName, conversationId), payload);
  } catch (err) {
    if (!isPermissionError(err) || collectionName === LEGACY_COLLECTION) throw err;
    await updateDoc(doc(db, 'workspaces', workspaceId, LEGACY_COLLECTION, conversationId), payload);
    rememberCollection(conversationId, LEGACY_COLLECTION);
  }
}

/** Migrate-friendly: also read legacy ai_chats if no new conversations exist. */
export async function loadLegacyAiChats(
  workspaceId: string,
  lawyerId: string,
  caseId: string,
): Promise<LexCounselConversation[]> {
  const snap = await getDocs(
    query(
      conversationsRef(workspaceId, LEGACY_COLLECTION),
      where('caseId', '==', caseId),
      where('lawyerId', '==', lawyerId),
      limit(50),
    ),
  );
  const chats = snap.docs.map((d) => {
    rememberCollection(d.id, LEGACY_COLLECTION);
    const data = d.data();
    return {
      id: d.id,
      caseId,
      lawyerId,
      title: String(data.title || 'Untitled chat'),
      history: Array.isArray(data.history) ? data.history : [],
      createdAt: String(data.createdAt || ''),
      updatedAt: String(data.updatedAt || ''),
    };
  });
  return sortByUpdatedDesc(chats);
}
